import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Upload, Download, CheckSquare, Square, FileUp, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency, calculateInvoiceTotal, API_BASE } from '../lib/api';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { isViewer } = useAuth();

  const fetchInvoices = () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    api.get('/invoices', { params }).then(res => setInvoices(res.data));
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // Client-side filter by invoice date (from/to). Backend doesn't support this yet.
  const filteredInvoices = invoices.filter(inv => {
    if (fromDate && inv.date < fromDate) return false;
    if (toDate && inv.date > toDate) return false;
    return true;
  });

  const filteredTotal = filteredInvoices.reduce((sum, inv) => sum + calculateInvoiceTotal(inv).total, 0);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filteredInvoices.length) setSelected(new Set());
    else setSelected(new Set(filteredInvoices.map(i => i.id)));
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    if (!window.confirm(`Apply ${action.replace('_', ' ')} to ${selected.size} invoice(s)?`)) return;
    setBulkBusy(true);
    try {
      await api.post('/invoices/bulk-action', { invoice_ids: Array.from(selected), action });
      setSelected(new Set());
      fetchInvoices();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} invoice(s) and their payments? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await api.post('/invoices/bulk-delete', { invoice_ids: Array.from(selected), action: 'delete' });
      setSelected(new Set());
      fetchInvoices();
      alert(`${res.data.deleted} invoice(s) deleted.`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteOne = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no} and its payments? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      setSelected(prev => { const n = new Set(prev); n.delete(inv.id); return n; });
      fetchInvoices();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const downloadZip = async () => {
    if (selected.size === 0) return;
    const url = `${API_BASE}/invoices/bulk-zip`;
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoice_ids: Array.from(selected), action: 'zip' }) });
    if (!res.ok) { alert('ZIP download failed'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'invoices.zip';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCsv = () => {
    window.open(`${API_BASE}/exports/invoices.csv${statusFilter ? '' : ''}`, '_blank');
  };

  const downloadImportTemplate = () => {
    window.open(`${API_BASE}/invoices/import-csv/template`, '_blank');
  };

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/invoices/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      fetchInvoices();
    } catch (err) {
      setImportResult({ imported: 0, skipped: 0, errors: [{ row: '-', msg: err.response?.data?.detail || err.message }], payments_created: 0 });
    } finally {
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Layout
      title="Invoices"
      action={
        <div className="flex gap-2">
          <button data-testid="export-invoices-csv" onClick={exportCsv} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export CSV
          </button>
          {!isViewer && (
            <>
              <button data-testid="csv-template-btn" onClick={downloadImportTemplate} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors" title="Download a sample CSV you can fill in">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Template
              </button>
              <label data-testid="csv-import-btn" className="bg-transparent text-maroon border border-maroon hover:bg-cream px-4 py-2 font-heading text-sm flex items-center transition-colors cursor-pointer">
                <FileUp className="w-4 h-4 mr-2" strokeWidth={1.5} /> {importBusy ? 'Importing…' : 'Import Historical CSV'}
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} disabled={importBusy} />
              </label>
              <button data-testid="upload-invoice-button" onClick={() => navigate('/invoices/upload')} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-4 py-2 font-heading text-sm flex items-center transition-colors">
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Upload PDF
              </button>
              <button
                data-testid="new-invoice-button"
                onClick={() => navigate('/invoices/new')}
                className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-base transition-colors flex items-center"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={1} /> New Invoice
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="bg-surface border border-border p-6">
        {importResult && (
          <div data-testid="import-result" className={`mb-4 p-4 border ${importResult.imported > 0 ? 'border-success/40 bg-success/5' : 'border-maroon/30 bg-maroon/5'}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-heading text-sm text-text-primary">
                Imported {importResult.imported} invoice{importResult.imported === 1 ? '' : 's'} · {importResult.payments_created} payment{importResult.payments_created === 1 ? '' : 's'} created · {importResult.skipped} skipped
              </p>
              <button onClick={() => setImportResult(null)} className="text-text-secondary hover:text-maroon font-body text-xs uppercase tracking-widest">Dismiss</button>
            </div>
            {importResult.errors?.length > 0 && (
              <details className="font-body text-xs text-text-secondary">
                <summary className="cursor-pointer hover:text-maroon">View {importResult.errors.length} error(s)</summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.map((er, i) => (
                    <li key={i}>Row {er.row}: {er.msg}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <div className="flex gap-3 mb-6 flex-wrap items-end">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" strokeWidth={1} />
            <input
              data-testid="invoice-search-input"
              type="text"
              placeholder="Search by invoice number or client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent border border-border focus:border-maroon outline-none pl-10 pr-3 py-2 font-body text-sm transition-colors"
            />
          </div>
          <select
            data-testid="invoice-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
          </select>
          <div>
            <label className="block font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">From</label>
            <input
              data-testid="invoice-from-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm"
            />
          </div>
          <div>
            <label className="block font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">To</label>
            <input
              data-testid="invoice-to-date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm"
            />
          </div>
          {(fromDate || toDate || statusFilter || search) && (
            <button
              data-testid="clear-filters"
              onClick={() => { setFromDate(''); setToDate(''); setStatusFilter(''); setSearch(''); }}
              className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover py-2"
            >
              Clear
            </button>
          )}
        </div>

        {/* Filtered total row */}
        <div className="flex items-baseline justify-end gap-4 mb-4 py-3 border-y border-border">
          <span className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">
            {statusFilter ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} · ` : ''}{filteredInvoices.length} invoice{filteredInvoices.length === 1 ? '' : 's'}
            {(fromDate || toDate) && ` · ${fromDate || '…'} → ${toDate || '…'}`}
          </span>
          <span className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Total</span>
          <span data-testid="invoices-filtered-total" className="font-numbers text-xl text-maroon tabular-nums">
            {formatCurrency(filteredTotal)}
          </span>
        </div>

        {!isViewer && selected.size > 0 && (
          <div data-testid="bulk-action-bar" className="flex items-center gap-3 mb-4 px-4 py-3 bg-cream border border-maroon/30">
            <span className="font-body text-sm text-text-primary">{selected.size} selected</span>
            <span className="text-text-secondary">|</span>
            <button data-testid="bulk-mark-sent" disabled={bulkBusy} onClick={() => bulkAction('mark_sent')} className="font-heading text-sm text-maroon hover:underline">Mark Sent</button>
            <button data-testid="bulk-mark-paid" disabled={bulkBusy} onClick={() => bulkAction('mark_paid')} className="font-heading text-sm text-maroon hover:underline">Mark Paid</button>
            <button data-testid="bulk-mark-pending" disabled={bulkBusy} onClick={() => bulkAction('mark_pending')} className="font-heading text-sm text-maroon hover:underline">Mark Pending</button>
            <button data-testid="bulk-download-zip" onClick={downloadZip} className="font-heading text-sm text-maroon hover:underline">Download ZIP</button>
            <button data-testid="bulk-delete-invoices" disabled={bulkBusy} onClick={bulkDelete} className="font-heading text-sm text-maroon hover:underline">Delete</button>
            <button data-testid="bulk-clear" onClick={() => setSelected(new Set())} className="ml-auto font-body text-xs text-text-secondary hover:text-maroon">Clear</button>
          </div>
        )}

        <table className="w-full">
          <thead>
            <tr>
              <th className="border-b border-border pb-3 w-8">
                {!isViewer && (
                  <button data-testid="select-all-invoices" onClick={toggleAll}>
                    {selected.size > 0 && selected.size === filteredInvoices.length ? <CheckSquare className="w-4 h-4 text-maroon" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-text-secondary" strokeWidth={1.5} />}
                  </button>
                )}
              </th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice No</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Company</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-center">Status</th>
              {!isViewer && (
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map(inv => {
              const { total } = calculateInvoiceTotal(inv);
              const isSel = selected.has(inv.id);
              return (
                <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`} className={`border-b border-border hover:bg-cream cursor-pointer transition-colors ${isSel ? 'bg-cream' : ''}`}>
                  <td className="py-4" onClick={(e) => e.stopPropagation()}>
                    {!isViewer && (
                      <button data-testid={`select-invoice-${inv.invoice_no}`} onClick={() => toggleOne(inv.id)}>
                        {isSel ? <CheckSquare className="w-4 h-4 text-maroon" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-text-secondary" strokeWidth={1.5} />}
                      </button>
                    )}
                  </td>
                  <td className="py-4 font-body text-sm text-text-primary" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    {inv.invoice_no}
                    {inv.source === 'uploaded' && (
                      <span data-testid={`uploaded-badge-${inv.invoice_no}`} className="ml-2 inline-block px-1.5 py-0.5 bg-border/40 border border-border font-body text-[10px] uppercase tracking-wider text-text-secondary">Uploaded</span>
                    )}
                  </td>
                  <td className="py-4 font-body text-sm text-text-secondary" onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.date}</td>
                  <td className="py-4 font-body text-sm text-text-primary" onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.client_name}</td>
                  <td className="py-4 font-body text-sm text-text-secondary" onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.company_name}</td>
                  <td className="py-4 font-numbers text-sm text-text-primary text-right tabular-nums" onClick={() => navigate(`/invoices/${inv.id}`)}>{formatCurrency(total)}</td>
                  <td className="py-4 text-center" onClick={() => navigate(`/invoices/${inv.id}`)}><StatusBadge status={inv.status} /></td>
                  {!isViewer && (
                    <td className="py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`delete-invoice-${inv.invoice_no}`}
                        onClick={() => deleteOne(inv)}
                        className="text-text-secondary hover:text-maroon transition-colors"
                        title={`Delete ${inv.invoice_no}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {filteredInvoices.length === 0 && (
              <tr><td colSpan={isViewer ? 7 : 8} className="py-8 text-center text-text-secondary font-body text-sm">No invoices found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
};

export default Invoices;
